import {
  ArrowDown,
  ArrowUp,
  ExternalLink,
  ImagePlus,
  LogOut,
  Plus,
  Save,
  Trash2
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  clearToken,
  fetchAdminContent,
  getToken,
  loginAdmin,
  saveAdminContent,
  setToken,
  uploadImage
} from './api.js';
import { makeId, moveItem } from './contentUtils.js';

const tabs = [
  { id: 'main', label: 'Главное' },
  { id: 'services', label: 'Услуги' },
  { id: 'gallery', label: 'Работы' },
  { id: 'materials', label: 'Материалы' },
  { id: 'contacts', label: 'Контакты' }
];

function clone(value) {
  return structuredClone(value);
}

function Field({ label, value, onChange, type = 'text', rows = 3, autoComplete }) {
  const id = `${label}-${Math.random().toString(36).slice(2)}`;

  return (
    <label className="admin-field" htmlFor={id}>
      <span>{label}</span>
      {type === 'textarea' ? (
        <textarea id={id} rows={rows} value={value || ''} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input
          id={id}
          type={type}
          value={value || ''}
          autoComplete={autoComplete}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </label>
  );
}

function ImageField({ label, value, onChange, token, onError }) {
  const [uploading, setUploading] = useState(false);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploading(true);
    try {
      const result = await uploadImage(token, file);
      onChange(result.url);
    } catch (error) {
      onError(error.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  return (
    <div className="admin-image-field">
      <Field label={label} value={value} onChange={onChange} />
      <div className="admin-image-row">
        {value ? <img src={value} alt="" /> : <span className="image-placeholder">Нет изображения</span>}
        <label className="button subtle">
          <ImagePlus size={17} />
          {uploading ? 'Загрузка...' : 'Загрузить'}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleFile} />
        </label>
      </div>
    </div>
  );
}

function CollectionEditor({ title, items, fields, addLabel, onChange, createItem, token, onError }) {
  function updateItem(index, key, value) {
    const nextItems = clone(items);
    nextItems[index] = { ...nextItems[index], [key]: value };
    onChange(nextItems);
  }

  function removeItem(index) {
    onChange(items.filter((_, itemIndex) => itemIndex !== index));
  }

  return (
    <section className="admin-card">
      <div className="admin-card-head">
        <h2>{title}</h2>
        <button className="button secondary" type="button" onClick={() => onChange([...items, createItem()])}>
          <Plus size={17} />
          {addLabel}
        </button>
      </div>

      <div className="collection-list">
        {items.map((item, index) => (
          <article className="collection-item" key={item.id || index}>
            <div className="collection-toolbar">
              <strong>{item.title || item.name || `Элемент ${index + 1}`}</strong>
              <span>
                <button type="button" onClick={() => onChange(moveItem(items, index, -1))} aria-label="Выше">
                  <ArrowUp size={16} />
                </button>
                <button type="button" onClick={() => onChange(moveItem(items, index, 1))} aria-label="Ниже">
                  <ArrowDown size={16} />
                </button>
                <button type="button" onClick={() => removeItem(index)} aria-label="Удалить">
                  <Trash2 size={16} />
                </button>
              </span>
            </div>

            <div className="collection-fields">
              {fields.map((field) =>
                field.type === 'image' ? (
                  <ImageField
                    key={field.key}
                    label={field.label}
                    value={item[field.key]}
                    token={token}
                    onError={onError}
                    onChange={(value) => updateItem(index, field.key, value)}
                  />
                ) : (
                  <Field
                    key={field.key}
                    label={field.label}
                    value={item[field.key]}
                    type={field.type}
                    rows={field.rows}
                    onChange={(value) => updateItem(index, field.key, value)}
                  />
                )
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const session = await loginAdmin({ username, password });
      setToken(session.token);
      onLogin(session.token);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="admin-login">
      <form onSubmit={handleSubmit}>
        <p className="eyebrow">Админ-панель</p>
        <h1>Вход</h1>
        <Field label="Логин" value={username} autoComplete="username" onChange={setUsername} />
        <Field
          label="Пароль"
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={setPassword}
        />
        {error ? <div className="admin-alert error">{error}</div> : null}
        <button className="button primary full" type="submit" disabled={loading}>
          {loading ? 'Проверяем...' : 'Войти'}
        </button>
      </form>
    </main>
  );
}

export function Admin({ initialContent, onContentChange }) {
  const [token, setSessionToken] = useState(getToken());
  const [draft, setDraft] = useState(initialContent);
  const [activeTab, setActiveTab] = useState('main');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const hasChanges = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initialContent), [draft, initialContent]);

  useEffect(() => {
    if (!token) {
      return;
    }

    fetchAdminContent(token)
      .then((content) => {
        setDraft(content);
        onContentChange(content);
      })
      .catch(() => {
        clearToken();
        setSessionToken('');
      });
  }, [token, onContentChange]);

  function setNested(path, value) {
    setDraft((current) => {
      const next = clone(current);
      let cursor = next;
      for (const segment of path.slice(0, -1)) {
        cursor = cursor[segment];
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  }

  function setList(path, value) {
    setNested(path, value);
  }

  async function saveDraft() {
    setSaving(true);
    setError('');
    setStatus('');

    try {
      const saved = await saveAdminContent(token, draft);
      setDraft(saved);
      onContentChange(saved);
      setStatus('Сохранено');
    } catch (requestError) {
      setError(requestError.message);
      if (requestError.message.includes('авториза')) {
        clearToken();
        setSessionToken('');
      }
    } finally {
      setSaving(false);
    }
  }

  function logout() {
    clearToken();
    setSessionToken('');
  }

  if (!token) {
    return <LoginScreen onLogin={setSessionToken} />;
  }

  return (
    <main className="admin-shell">
      <aside className="admin-sidebar">
        <div>
          <p className="eyebrow">Управление сайтом</p>
          <h1>Гравировка</h1>
        </div>
        <nav>
          {tabs.map((tab) => (
            <button
              className={activeTab === tab.id ? 'active' : ''}
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="admin-side-actions">
          <a className="button subtle" href="/" target="_blank" rel="noreferrer">
            <ExternalLink size={17} />
            Открыть сайт
          </a>
          <button className="button subtle" type="button" onClick={logout}>
            <LogOut size={17} />
            Выйти
          </button>
        </div>
      </aside>

      <section className="admin-workspace">
        <header className="admin-topline">
          <div>
            <span>{hasChanges ? 'Есть несохранённые изменения' : 'Изменений нет'}</span>
            {status ? <strong>{status}</strong> : null}
          </div>
          <button className="button primary" type="button" onClick={saveDraft} disabled={saving}>
            <Save size={18} />
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </header>

        {error ? <div className="admin-alert error">{error}</div> : null}

        {activeTab === 'main' ? (
          <div className="admin-grid two">
            <section className="admin-card">
              <h2>Бренд и первый экран</h2>
              <Field label="Название" value={draft.brand.name} onChange={(value) => setNested(['brand', 'name'], value)} />
              <Field
                label="Короткое название"
                value={draft.brand.shortName}
                onChange={(value) => setNested(['brand', 'shortName'], value)}
              />
              <Field
                label="Подзаголовок"
                value={draft.brand.subtitle}
                onChange={(value) => setNested(['brand', 'subtitle'], value)}
              />
              <ImageField
                label="Логотип"
                value={draft.brand.logoUrl}
                token={token}
                onError={setError}
                onChange={(value) => setNested(['brand', 'logoUrl'], value)}
              />
              <Field label="Заголовок" value={draft.hero.title} onChange={(value) => setNested(['hero', 'title'], value)} />
              <Field
                label="Акцент"
                value={draft.hero.eyebrow}
                onChange={(value) => setNested(['hero', 'eyebrow'], value)}
              />
              <Field
                label="Описание"
                type="textarea"
                rows={4}
                value={draft.hero.description}
                onChange={(value) => setNested(['hero', 'description'], value)}
              />
              <ImageField
                label="Изображение первого экрана"
                value={draft.hero.imageUrl}
                token={token}
                onError={setError}
                onChange={(value) => setNested(['hero', 'imageUrl'], value)}
              />
            </section>

            <section className="admin-card">
              <h2>О мастере</h2>
              <Field label="Заголовок" value={draft.about.title} onChange={(value) => setNested(['about', 'title'], value)} />
              <Field label="Акцент" value={draft.about.accent} onChange={(value) => setNested(['about', 'accent'], value)} />
              <Field
                label="Описание"
                type="textarea"
                rows={6}
                value={draft.about.description}
                onChange={(value) => setNested(['about', 'description'], value)}
              />
              <Field
                label="Основная кнопка"
                value={draft.hero.primaryCta}
                onChange={(value) => setNested(['hero', 'primaryCta'], value)}
              />
              <Field
                label="Вторая кнопка"
                value={draft.hero.secondaryCta}
                onChange={(value) => setNested(['hero', 'secondaryCta'], value)}
              />
            </section>
          </div>
        ) : null}

        {activeTab === 'services' ? (
          <CollectionEditor
            title="Услуги"
            items={draft.services}
            addLabel="Добавить услугу"
            token={token}
            onError={setError}
            onChange={(items) => setList(['services'], items)}
            createItem={() => ({
              id: makeId('service'),
              title: 'Новая услуга',
              description: 'Описание услуги',
              imageUrl: '/assets/service-plate.png'
            })}
            fields={[
              { key: 'title', label: 'Название' },
              { key: 'description', label: 'Описание', type: 'textarea', rows: 4 },
              { key: 'imageUrl', label: 'Изображение', type: 'image' }
            ]}
          />
        ) : null}

        {activeTab === 'gallery' ? (
          <CollectionEditor
            title="Примеры работ"
            items={draft.gallery}
            addLabel="Добавить работу"
            token={token}
            onError={setError}
            onChange={(items) => setList(['gallery'], items)}
            createItem={() => ({
              id: makeId('work'),
              title: 'Новая работа',
              imageUrl: '/assets/gallery-01.png'
            })}
            fields={[
              { key: 'title', label: 'Название' },
              { key: 'imageUrl', label: 'Изображение', type: 'image' }
            ]}
          />
        ) : null}

        {activeTab === 'materials' ? (
          <CollectionEditor
            title="Материалы"
            items={draft.materials}
            addLabel="Добавить материал"
            token={token}
            onError={setError}
            onChange={(items) => setList(['materials'], items)}
            createItem={() => ({
              id: makeId('material'),
              name: 'Новый материал',
              imageUrl: '/assets/material-steel.png'
            })}
            fields={[
              { key: 'name', label: 'Название' },
              { key: 'imageUrl', label: 'Изображение', type: 'image' }
            ]}
          />
        ) : null}

        {activeTab === 'contacts' ? (
          <div className="admin-grid two">
            <section className="admin-card">
              <h2>Контакты</h2>
              <Field
                label="Телефоны"
                type="textarea"
                rows={4}
                value={draft.contacts.phones.join('\n')}
                onChange={(value) => setNested(['contacts', 'phones'], value.split('\n'))}
              />
              <Field
                label="WhatsApp"
                value={draft.contacts.whatsapp}
                onChange={(value) => setNested(['contacts', 'whatsapp'], value)}
              />
              <Field label="Email" value={draft.contacts.email} onChange={(value) => setNested(['contacts', 'email'], value)} />
              <Field label="MAX ссылка" value={draft.contacts.maxUrl} onChange={(value) => setNested(['contacts', 'maxUrl'], value)} />
            </section>
            <section className="admin-card">
              <h2>Подвал</h2>
              <Field
                label="Копирайт"
                value={draft.footer.copyright}
                onChange={(value) => setNested(['footer', 'copyright'], value)}
              />
              <Field label="Примечание" value={draft.footer.note} onChange={(value) => setNested(['footer', 'note'], value)} />
            </section>
          </div>
        ) : null}
      </section>
    </main>
  );
}
